import { Component, Injector, OnInit, ViewChild } from '@angular/core';
import { Router, UrlCreationOptions } from '@angular/router';

import { takeUntil } from 'rxjs/operators';

import { RowClickedEvent } from 'ag-grid-community';
import { IServerSideGetRowsParams } from 'ag-grid-enterprise';

import FileSaver from 'file-saver';

import { ListComponent } from '@components/list/list.component';
import { SearchBoxComponent } from '@components/search-box/search-box.component';
import { TableComponent } from '@components/table/table.component';
import { ReportApi } from '@api/report.api';
import { OrderApi } from '@api/order.api';
import { ReportService } from '@services/report.service';
import { HeaderService } from '@services/header.service';
import { QueryParams } from '@models/api';
import { TableData } from '@models/table';
import { ExportParams, GetDataParams, SearchBoxConfigParams } from '@models/report';

@Component({
  selector: 'app-report',
  templateUrl: './report.component.html',
  styleUrls: ['./report.component.scss']
})
export class ReportComponent<Type> extends ListComponent<Type> implements OnInit {
  @ViewChild(SearchBoxComponent, {static: true}) public override searchBoxComp!: SearchBoxComponent;
  @ViewChild(TableComponent) protected override tableComp!: TableComponent<Type>;

  protected router: Router;
  protected api: ReportApi | OrderApi;
  protected service: ReportService;
  protected headerService: HeaderService;

  protected getSearchBoxConfigMethod: string;
  protected getTableConfigMethod: string;
  protected prepareQueryParamsMethod: string;
  protected getTableDataMethod: string;
  protected downloadReportMethod: string;

  protected override title: string = 'report title';
  protected readonly baseReportName: string = 'Report Name';
  protected get reportName(): string {
    return this.baseReportName;
  }

  protected filteredColIds: Array<string> = [];
  protected searchBoxConfigParams: SearchBoxConfigParams = { };
  protected exportParams: ExportParams = { };
  protected getDataParams: GetDataParams = { };
  protected extraQueryParams: QueryParams = { };

  constructor(injector: Injector) {
    super(injector);

    this.router = injector.get(Router);
    this.api = injector.get(ReportApi);
    this.service = injector.get(ReportService);
    this.headerService = injector.get(HeaderService);

    this.setMethods();
  }

  ngOnInit(): void {
    this.setUpReport();

    this.headerService.nextTitle(this.title);

    this.setSearchBoxControls();
  }

  protected onExportCSVBtnClick(extraQueryParams: QueryParams = { }): void {
    const queryParams: QueryParams = {
      ...this.getQueryParams(),
      export: 'csv',
      ...extraQueryParams
    };

    this.api[this.downloadReportMethod](...Object.values(this.exportParams), 'text', queryParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (data: string) => FileSaver.saveAs(
          new Blob([data], {type: 'text/csv'}),
          `${this.reportName}.csv`
        ),
        (error: string | Error) => { }
      );
  }

  protected onExportPDFBtnClick(extraQueryParams: QueryParams = { }): void {
    const queryParams: QueryParams = {
      ...this.getQueryParams(),
      export: 'pdf'
    };

    this.api[this.downloadReportMethod]('arraybuffer', queryParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (data: ArrayBuffer) => FileSaver.saveAs(
          new Blob([data], {type: 'application/pdf;charset=utf-8'}),
          `${this.reportName}.pdf`
        ),
        (error: string | Error) => { }
      );
  }

  public onRowClicked(event: RowClickedEvent, commands?: Array<any>, options?: UrlCreationOptions): void {
    const filteredColIds: Array<string> = this.filteredColIds,
          currentColId: string = this.tableComp.gridApi.getFocusedCell().column.getColId(),
          url: string = this.router.serializeUrl(
            this.router.createUrlTree(
              commands ? commands : ['order-edit', event.data.id],
              options
            )
          );
    if (filteredColIds.some((id: string) => id === currentColId)) { return; }

    window.open(url, '_blank');
  }

  protected setMethods() {
    this.getSearchBoxConfigMethod = '';
    this.getTableConfigMethod = '';
    this.prepareQueryParamsMethod = '';
    this.getTableDataMethod = '';
    this.downloadReportMethod = '';
  }

  protected setUpReport() {
    this.searchBoxConfig = this.service[this.getSearchBoxConfigMethod](...Object.values(this.searchBoxConfigParams));
    this.filterSectionConfig = this.searchBoxConfig.filterSection;

    this.tableConfig = this.service[this.getTableConfigMethod]();
    this.serverSideDatasource = {
      getRows: (params: IServerSideGetRowsParams): void => {
        this.getData(params);
      }
    };
  }

  protected getData(params: IServerSideGetRowsParams): void {
    this.serverSideGetRowsParams = params;
    const queryParams: QueryParams = {
      ...this.extraQueryParams,
      ...this.getQueryParams()
    };

    this.api[this.getTableDataMethod](...Object.values(this.getDataParams), queryParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (data: TableData<Type>) => {
          const rowData: Array<Type> = data.items ?? data.orders

          params.success({
            rowData,
            rowCount: data.paginator.total
          });

          this.tableComp.toggleNoRowsOverlay(rowData.length ? 'hide' : 'show');
        },
        (error: string) => params.fail()
      );
  }

  protected setSearchBoxControls(): void { }

  protected getQueryParams(): QueryParams {
    return this.service[this.prepareQueryParamsMethod](this.searchBoxComp.getValue(), this.serverSideGetRowsParams);
  }
}
